/**
 * Part swaps (owner, 22 Oct 2026): "suppose in on wheels a person has 2 vehicle
 * give him a option to swap battery between vehicle and if it does it price
 * will also change according to battery. battery can be swapped at head office
 * as well along with motor controller and charger at head store with one
 * vehicle or from the factory battery stock create a dedicated system for it
 * also surbhi can also swap or change battery of a vehicle."
 *
 * His answers, the same day:
 *   - a Bright on Wheels counter swaps the batteries of two of its own unsold
 *     vehicles straight away — no approval — and the office sees every swap;
 *   - the price follows the battery both ways (sale.ts batteryUpgradeFee);
 *   - a battery taken out for one from the factory store goes back on the
 *     factory's good shelf;
 *   - at head office, and from the factory store, it is the service desk's
 *     (Arun's) — `service:desk`.
 *
 * The motor joined on 23 Oct 2026 (owner: "i want battery , motor , controller
 * and charger system change in head office as well here we can swap as well
 * or change it from our stock of factory"): head office's parts are four.
 *
 * A swap moves the part's own keys on `Vehicle.extra` — the battery's company,
 * size and serials, the motor's watts and company, the charger's model and
 * serial, the controller's serial —
 * so every reader of them (the price, the stock filters, the serial search,
 * the papers) follows. Bright on Wheels' copy of a vehicle changes; Bright
 * Autozone's record of the same chassis stays as it was dispatched.
 */
import { ORG_IDS, isHeadAdminDesk } from './domain.js';
import { BATTERY_SERIAL_EXTRA_KEYS } from './dispatch-form.js';

export const SWAP_PARTS = ['BATTERY', 'MOTOR', 'CONTROLLER', 'CHARGER'] as const;
export type SwapPart = (typeof SWAP_PARTS)[number];
export const SWAP_PART_LABELS: Record<SwapPart, string> = { BATTERY: 'Battery', MOTOR: 'Motor', CONTROLLER: 'Motor controller', CHARGER: 'Charger' };

/**
 * Between two vehicles; the part fitted from the factory store with the old one put back on its shelf;
 * or a battery fitted from the HEAD OFFICE store, the old one back in it (23 Oct 2026).
 */
export const SWAP_KINDS = ['BETWEEN', 'FACTORY', 'STORE'] as const;
export type SwapKind = (typeof SWAP_KINDS)[number];

/** What moves with each part: its keys on `Vehicle.extra`, as the dispatch bay writes them. */
export const SWAP_PART_KEYS: Record<SwapPart, readonly string[]> = {
  BATTERY: ['battery_company', 'battery_ah', ...BATTERY_SERIAL_EXTRA_KEYS],
  // The motor carries no serial on the dispatch form: its watts and its company (23 Oct 2026). The
  // factory store scans one when it assigns the motor to a chassis (26 Oct 2026); a swap carries it.
  MOTOR: ['motor', 'motor_company', 'motor_serial_no'],
  CONTROLLER: ['controller_serial_no'],
  CHARGER: ['charger_model', 'charger_serial_no'],
};

/**
 * Written for a key the incoming part does not carry — a lead set's second to
 * fourth serials on a vehicle given one lithium pack. A blank would let the
 * papers fall back to Bright Autozone's record of the chassis (mergeSpec), and
 * so print the battery that left it.
 */
export const SWAP_CLEARED = '—';

/** The keys a swap has written, kept on the vehicle so a sheet sync never puts the old part back. */
export const SWAPPED_KEYS_KEY = 'swapped_keys';

/** The books a swap is recorded in: Bright on Wheels' counters and office, and the head office of Bright Autozone. */
export const SWAP_BOOKS: readonly string[] = [ORG_IDS.bow, ORG_IDS.wholesale];

/** Up to four 12 V batteries make a lead-acid set; lithium is one pack. */
export const SWAP_MAX_PIECES = 4;

/**
 * The office side of Bright on Wheels: whoever is switched on to approve
 * transfers (Surbhi, and Abhishek in her absence), and the owner's desks.
 * They swap the batteries of any two unsold vehicles in the book.
 */
export function swapsAsOffice(user: { email: string; approvesTransfers?: boolean | null; roleKey?: string | null } | null | undefined): boolean {
  if (!user) return false;
  // The switch counts on an administrator only, as transfers read it; the server has checked that already.
  const switched = Boolean(user.approvesTransfers) && (user.roleKey === undefined || user.roleKey === 'ADMINISTRATOR');
  return switched || isHeadAdminDesk(user);
}

/** Whether a sign-in keeps the Swaps page somewhere: the office or the service desk, reaching Bright on Wheels or head office. */
export function seesSwaps(user: {
  email: string;
  approvesTransfers?: boolean | null;
  roleKey?: string | null;
  homeBook: string;
  allBooks?: boolean | null;
  serviceDesk: boolean;
  scoped: boolean;
}): boolean {
  if (user.scoped) return false;
  const reaches = (book: string) => Boolean(user.allBooks) || user.homeBook === book;
  // The dispatch desk swaps head office batteries (27 Oct 2026: "move battery swap to rahul dispatch desk").
  if (user.roleKey === 'DISPATCH' && reaches(ORG_IDS.wholesale)) return true;
  return (swapsAsOffice(user) && reaches(ORG_IDS.bow)) || (user.serviceDesk && SWAP_BOOKS.some(reaches));
}

/** A part as it stood on a vehicle: its keys, blank ones left out. */
export type SwapPartState = Record<string, string>;

/** The part as it stands on a vehicle's extra: its keys, blank and cleared ones left out. */
export function swapPartOf(part: SwapPart, extra: Record<string, unknown> | null | undefined): SwapPartState {
  const state: SwapPartState = {};
  for (const key of SWAP_PART_KEYS[part]) {
    const value = extra?.[key];
    if (typeof value === 'string' && value.trim() !== '' && value !== SWAP_CLEARED) state[key] = value.trim();
    else if (typeof value === 'number') state[key] = String(value);
  }
  return state;
}

/** One vehicle of a swap, before and after. */
export interface SwapSide {
  vehicleId: string;
  chassisNo: string;
  model: string | null;
  /** Who held it: a counter, the store, or head office. */
  holder: string | null;
  /** Its party then — a counter sees only its own side's holder and prices. */
  holderPartyId: string | null;
  variantBefore: string | null;
  variantAfter: string | null;
  before: SwapPartState;
  after: SwapPartState;
  /** The counter's price either side, where the vehicle has one (Bright on Wheels stock). */
  priceBefore: number | null;
  priceAfter: number | null;
}

export interface SwapRow {
  id: string;
  kind: SwapKind;
  part: SwapPart;
  at: string;
  by: string | null;
  note: string | null;
  /** One side for a fit from the factory store, two for a swap between vehicles. */
  sides: SwapSide[];
  /** From the factory store: the part taken out, and the shelf the old one went back to. */
  fitted: { itemId: string; name: string; pieces: number } | null;
  returned: { itemId: string; name: string; pieces: number } | null;
}

/** A vehicle the part can be swapped with, and what each price becomes. */
export interface SwapCandidate {
  vehicleId: string;
  chassisNo: string;
  model: string | null;
  variant: string | null;
  holder: string | null;
  part: SwapPartState;
  /** This candidate's price now and after taking the other vehicle's part. */
  priceNow: number | null;
  priceAfter: number | null;
  /** The vehicle the swap was opened from: its price after taking this candidate's part. */
  thisPriceAfter: number | null;
  /** Why it cannot be swapped now, when it cannot. */
  blocked: string | null;
}

/** A vehicle as a swap opens on it. */
export interface SwapCheck {
  /** Why nothing may be swapped on it now, or null. */
  blocked: string | null;
  /** What it carries now, as the papers read it. */
  part: SwapPartState;
  /** The old pieces that would come off: a lead set's four, one, or none. */
  returnedPieces: number;
}

export interface SwapCandidates {
  vehicle: {
    vehicleId: string;
    chassisNo: string;
    model: string | null;
    variant: string | null;
    holder: string | null;
    part: SwapPartState;
    priceNow: number | null;
    blocked: string | null;
  };
  candidates: SwapCandidate[];
  /** True when the list is a search's result (the office), not a counter's whole stock. */
  searched: boolean;
}

/** "140 AH Lithium 36 M · EASTMAN · serial 8812…" — a part in one line. */
export function swapPartLine(part: SwapPart, state: SwapPartState): string {
  const shown = (key: string) => (state[key] && state[key] !== SWAP_CLEARED ? state[key] : null);
  const serials = SWAP_PART_KEYS[part].filter((key) => key.includes('serial')).map(shown).filter(Boolean);
  const head =
    part === 'BATTERY'
      ? [shown('battery_ah'), shown('battery_company')]
      : part === 'MOTOR'
        ? [shown('motor'), shown('motor_company')]
        : part === 'CHARGER'
          ? [shown('charger_model')]
          : [];
  const words = [...head.filter(Boolean), serials.length ? `serial ${serials.join(', ')}` : null].filter(Boolean);
  return words.length ? words.join(' · ') : 'Not recorded';
}
